/**
 * CSRF double-submit primitives shared by every client that talks to a
 * state-changing judge-api endpoint.
 *
 * The server (judge-api/internal/middleware/csrf.go) enforces three layers
 * on unsafe methods; layer 3 is a synchronizer token that must appear BOTH
 * as the `judge_csrf` cookie and as the `x-csrf-token` header. The cookie is
 * minted by GET /api/auth/csrf-token.
 *
 * This module is the single source of truth for that handshake. It is
 * consumed by two callers:
 *
 *   - the Apollo link (shared/apollo/links/csrfLink.ts) for GraphQL /query
 *   - `csrfFetch` below, for the plain-`fetch` REST endpoints (/api/billing/*)
 *
 * Both MUST share one module so they share `mintInFlight`. Two independent
 * mint paths could race: each GETs /api/auth/csrf-token, the second
 * Set-Cookie overwrites the first, and a request that already read the older
 * value fails the constant-time compare as `token_mismatch`.
 */
import { ConfigConstants } from '../constants';
import { createLogger } from './logger';
const log = createLogger('csrf');
export const CSRF_COOKIE_NAME = 'judge_csrf';
export const CSRF_HEADER_NAME = 'x-csrf-token';
const CSRF_TOKEN_ENDPOINT = '/api/auth/csrf-token';

/**
 * Read the value of a cookie from document.cookie. Returns "" if the
 * cookie is absent, empty, or document is not available (SSR).
 */
export function readCookie(name: string): string {
  if (typeof document === 'undefined' || !document.cookie) {
    return '';
  }
  const prefix = `${name}=`;
  const parts = document.cookie.split(';');
  for (const raw of parts) {
    const c = raw.trim();
    if (c.startsWith(prefix)) {
      return decodeURIComponent(c.slice(prefix.length));
    }
  }
  return '';
}

/**
 * Resolve the absolute URL of the CSRF mint endpoint. Uses the same
 * resolution strategy as the GraphQL HttpLink so cross-origin
 * subdomain-based deployments hit the right host.
 */
function csrfTokenURL(): string {
  const base = ConfigConstants?.judgeapi_svc || '';
  return `${base}${CSRF_TOKEN_ENDPOINT}`;
}
let mintInFlight: Promise<void> | null = null;

/**
 * Fetch a fresh CSRF cookie from the server. The response sets a
 * Set-Cookie header that the browser stores; we do not need to read the
 * JSON body (the cookie is the source of truth). Concurrent callers
 * share the same in-flight promise so a parallel mutation burst only
 * mints one cookie.
 */
export async function fetchCSRFCookie(): Promise<void> {
  if (mintInFlight) {
    return mintInFlight;
  }
  mintInFlight = (async () => {
    try {
      const response = await fetch(csrfTokenURL(), {
        method: 'GET',
        credentials: 'include'
      });
      if (!response.ok) {
        throw new Error(`csrf-token mint failed: ${response.status}`);
      }
    } finally {
      // Clear the in-flight slot before returning so a subsequent
      // failure path (e.g. an expired session that voided the
      // cookie) can retry rather than poison every future mint.
      mintInFlight = null;
    }
  })();
  return mintInFlight;
}

/**
 * Methods the server treats as state-changing. Mirrors isUnsafeMethod in
 * judge-api/internal/middleware/csrf.go — GET/HEAD/OPTIONS bypass the gate
 * entirely, so we never pay a mint round-trip for them.
 */
function isUnsafeMethod(method: string): boolean {
  switch (method.toUpperCase()) {
    case 'POST':
    case 'PUT':
    case 'PATCH':
    case 'DELETE':
      return true;
    default:
      return false;
  }
}

/** Merge the synchronizer token into a plain header record. */
function withCSRFHeader(headers: Record<string, string> | undefined, token: string): Record<string, string> {
  const merged: Record<string, string> = {
    ...headers
  };
  if (token) {
    merged[CSRF_HEADER_NAME] = token;
  }
  return merged;
}

/**
 * `fetch` with the CSRF synchronizer token attached — the REST counterpart
 * of csrfLink. Use this for EVERY state-changing judge-api call made with
 * plain fetch; a bare fetch() to a gated endpoint is rejected 403
 * `csrf_rejected`.
 *
 * Sends `credentials: 'include'` by default (the gate authenticates the
 * session cookie it is defending). Callers may override any init field.
 *
 * When the cookie is already present the underlying fetch is invoked
 * SYNCHRONOUSLY, before this function returns its promise — no microtask
 * hop. That keeps the common path cheap and preserves the call-ordering
 * that synchronous tests and in-flight request bookkeeping rely on. Only
 * the cold path (no cookie yet) awaits a mint first.
 */
export function csrfFetch(input: RequestInfo | URL, init: RequestInit & {
  headers?: Record<string, string>;
} = {}): Promise<Response> {
  const method = init.method ?? 'GET';
  const send = (): Promise<Response> => fetch(input, {
    credentials: 'include',
    ...init,
    headers: withCSRFHeader(init.headers, readCookie(CSRF_COOKIE_NAME))
  });
  if (!isUnsafeMethod(method) || readCookie(CSRF_COOKIE_NAME)) {
    return send();
  }

  // Cold path: no cookie yet. Mint one, then send. A failed mint still
  // sends — the server's structured 403 is a better diagnostic than a
  // swallowed client-side error.
  return fetchCSRFCookie().catch((err: unknown) => {
    log.warn('csrf cookie mint failed; proceeding without token', {
      error: String(err)
    });
  }).then(send);
}

/**
 * Test-only: reset the in-flight mint promise so adjacent tests start
 * from a known state.
 */
export function _resetCSRFForTesting(): void {
  mintInFlight = null;
}